/**
 * Centralized time-zone utilities for the IntellSchools frontend.
 *
 * Rules (mirrors the backend `shared/time` module):
 *  - The API exchanges real moments as ISO-8601 UTC instants (e.g. "2026-03-08T13:10:00Z").
 *  - Calendar dates ("2026-09-01") and wall-clock times ("08:10") are exchanged as plain strings
 *    and are interpreted in an IANA time zone that the server supplies.
 *  - Components must never do manual offset arithmetic. Use these helpers.
 *
 * Temporal (via temporal-polyfill) is used because it models DST gaps/overlaps explicitly.
 */
import { Temporal } from 'temporal-polyfill';

export type Disambiguation = 'compatible' | 'earlier' | 'later' | 'reject';

/** Result of converting a local wall-clock date/time in a zone to an instant. */
export interface ZonedConversion {
  /** ISO-8601 UTC instant. */
  instant: string;
  /**
   * exact:        the wall-clock time exists exactly once in the zone.
   * nonexistent:  the wall-clock time falls in a DST gap (spring forward); it was shifted forward.
   * ambiguous:    the wall-clock time occurs twice (fall back); `disambiguation` chose one.
   */
  status: 'exact' | 'nonexistent' | 'ambiguous';
  /** Offset applied, e.g. "-04:00". */
  offset: string;
}

const PLAIN_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PLAIN_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

/** Legacy abbreviations are not valid authoritative zones (they ignore regional DST rules). */
const REJECTED_ABBREVIATIONS = new Set([
  'EST',
  'EDT',
  'CST',
  'CDT',
  'MST',
  'MDT',
  'PST',
  'PDT',
  'AKST',
  'HST',
  'EST5EDT',
  'CST6CDT',
  'MST7MDT',
  'PST8PDT',
]);

export function isValidTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone || REJECTED_ABBREVIATIONS.has(timeZone.toUpperCase())) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
    return timeZone === 'UTC' || timeZone.includes('/');
  } catch {
    return false;
  }
}

export function isPlainDate(value: string): boolean {
  if (!PLAIN_DATE_RE.test(value)) return false;
  try {
    Temporal.PlainDate.from(value, { overflow: 'reject' });
    return true;
  } catch {
    return false;
  }
}

export function isPlainTime(value: string): boolean {
  return PLAIN_TIME_RE.test(value);
}

/** Lists IANA zones supported by the runtime, for pickers. */
export function supportedTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  const zones = intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  return zones.includes('UTC') ? zones : ['UTC', ...zones];
}

/** The browser's zone, used only as a display hint (never authoritative). */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Converts a UTC instant to its local date and time in `timeZone`. */
export function instantToZonedParts(
  instant: string,
  timeZone: string,
): { date: string; time: string; offset: string } {
  const zdt = Temporal.Instant.from(instant).toZonedDateTimeISO(timeZone);
  return {
    date: zdt.toPlainDate().toString(),
    time: zdt.toPlainTime().toString({ smallestUnit: 'minute' }),
    offset: zdt.offset,
  };
}

/**
 * Converts a local calendar date + wall-clock time in `timeZone` to a UTC instant, reporting DST
 * gaps and overlaps explicitly so the UI can inform the user.
 */
export function zonedPartsToInstant(
  date: string,
  time: string,
  timeZone: string,
  disambiguation: Disambiguation = 'compatible',
): ZonedConversion {
  const plain = Temporal.PlainDateTime.from(`${date}T${time.length === 5 ? `${time}:00` : time}`);
  const earlier = plain.toZonedDateTime(timeZone, { disambiguation: 'earlier' });
  const later = plain.toZonedDateTime(timeZone, { disambiguation: 'later' });
  const chosen = plain.toZonedDateTime(timeZone, { disambiguation });

  let status: ZonedConversion['status'] = 'exact';
  if (!earlier.toPlainDateTime().equals(plain) || !later.toPlainDateTime().equals(plain)) {
    status = 'nonexistent';
  } else if (earlier.epochMilliseconds !== later.epochMilliseconds) {
    status = 'ambiguous';
  }

  return {
    instant: chosen.toInstant().toString(),
    status,
    offset: chosen.offset,
  };
}

/** Today's calendar date in `timeZone` (e.g. the school's "today" for attendance). */
export function todayInZone(timeZone: string, now: Date = new Date()): string {
  return Temporal.Instant.fromEpochMilliseconds(now.getTime())
    .toZonedDateTimeISO(timeZone)
    .toPlainDate()
    .toString();
}

export type InstantStyle = 'date' | 'time' | 'datetime' | 'datetime-long' | 'datetime-seconds';

const INSTANT_STYLES: Record<InstantStyle, Intl.DateTimeFormatOptions> = {
  date: { dateStyle: 'medium' },
  time: { timeStyle: 'short' },
  datetime: { dateStyle: 'medium', timeStyle: 'short' },
  'datetime-long': { dateStyle: 'full', timeStyle: 'short' },
  'datetime-seconds': {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  },
};

/** Formats a UTC instant for display in `timeZone`. */
export function formatInstant(
  instant: string | null | undefined,
  timeZone: string,
  options: { style?: InstantStyle; locale?: string; showZone?: boolean } = {},
): string {
  if (!instant) return '';
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return '';
  const base = INSTANT_STYLES[options.style ?? 'datetime'];
  // dateStyle/timeStyle cannot be combined with timeZoneName, so expand to explicit fields.
  const withZone: Intl.DateTimeFormatOptions = options.showZone
    ? {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(options.style === 'date' ? {} : { hour: 'numeric', minute: '2-digit' }),
        timeZoneName: 'short',
      }
    : base;
  return new Intl.DateTimeFormat(options.locale, { ...withZone, timeZone }).format(date);
}

/** Formats a plain calendar date. Plain dates have no zone: they are formatted as-is. */
export function formatPlainDate(
  date: string | null | undefined,
  options: { locale?: string; style?: 'short' | 'medium' | 'long' | 'full' } = {},
): string {
  if (!date) return '';
  if (!isPlainDate(date)) return date;
  return Temporal.PlainDate.from(date).toLocaleString(options.locale, {
    dateStyle: options.style ?? 'medium',
  });
}

/** Formats a plain wall-clock time ("08:10") using locale conventions (e.g. "8:10 AM"). */
export function formatPlainTime(time: string | null | undefined, locale?: string): string {
  if (!time) return '';
  if (!isPlainTime(time)) return time;
  return Temporal.PlainTime.from(time).toLocaleString(locale, { timeStyle: 'short' });
}

/** Short zone label for an instant, e.g. "EDT" or "GMT+2". */
export function timeZoneLabel(
  timeZone: string,
  instant: string = new Date().toISOString(),
  locale?: string,
): string {
  const parts = new Intl.DateTimeFormat(locale ?? 'en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(new Date(instant));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
}

/** Relative description ("5 minutes ago", "in 2 days"). */
export function formatRelative(instant: string, now: Date = new Date(), locale?: string): string {
  const diffMs = new Date(instant).getTime() - now.getTime();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(diffMs);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 24 * 3600e3],
    ['month', 30 * 24 * 3600e3],
    ['week', 7 * 24 * 3600e3],
    ['day', 24 * 3600e3],
    ['hour', 3600e3],
    ['minute', 60e3],
  ];
  for (const [unit, ms] of units) {
    if (abs >= ms) return rtf.format(Math.round(diffMs / ms), unit);
  }
  return rtf.format(Math.round(diffMs / 1000), 'second');
}
